import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, TooltipProps } from "recharts";
import styles from "./css/PieGraph.module.css";
import { LoadingWaves } from '../addons/LoadingWaves';
import { PrecentageDict } from "@/types";
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

interface PieGraphProps {
  data: PrecentageDict | undefined;
  colors: {
    [key: string]: string;
  };
  size?: 'default' | 'small' | 'large' | 'xsmall';
  showLegend?: boolean;
  year?: string;
  valueDisplay?: 'percentage' | 'number';
}

interface TooltipPayload {
  name: string;
  value: number;
  payload: {
    name: string;
    value: number;
  };
  fill: string;
  dataKey: string;
}

export const PieGraph: React.FC<PieGraphProps> = ({ 
  data, 
  colors,
  size = 'default',
  showLegend = false,
  year,
  valueDisplay = 'number'
}) => {
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Transform and sort the data from largest to smallest
  const chartData = data ? Object.entries(data)
    .map(([name, value]) => ({
      name,
      value
    }))
    .sort((a, b) => b.value - a.value) : [];

  // Get colors in the same order as data
  const colorValues = chartData.map(item => colors[item.name]);

  // Size configurations
  const sizeConfig = {
    default: {
      containerClass: styles.container,
      outerRadius: windowWidth > 1536 ? 90 : 80,
      height: windowWidth > 1536 ? 230 : 196,
      labelRadius: windowWidth > 1536 ? 100 : 95
    },
    xsmall: {
      containerClass: styles.containerXSmall,
      outerRadius: windowWidth > 1536 ? 40 : 35,
      height: windowWidth > 1536 ? 154 : 106,
      labelRadius: windowWidth > 1536 ? 46 : 42
    },
    small: {
      containerClass: styles.containerSmall,
      outerRadius: windowWidth > 1536 ? 52 : 37,
      height: windowWidth > 1536 ? 154 : 106,
      labelRadius: windowWidth > 1536 ? 58 : 44
    },
    large: {
      containerClass: styles.containerLarge,
      outerRadius: windowWidth > 1536 ? 115 : 100,
      height: windowWidth > 1536 ? 274 : 236,
      labelRadius: windowWidth > 1536 ? 115 : 100
    }
  };
  
  // Use the selected size configuration
  const currentSize = sizeConfig[size];

  // Add this formatter function at the component level
  const formatNumber = (num: number) => {
    if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toString();
  };

  // Add percentage formatter
  const calculatePercentage = (value: number | (string | number)[]) => {
    const numericValue = Array.isArray(value) ? 0 : Number(value);
    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    const percentage = ((numericValue / total) * 100).toFixed(1);
    return `${percentage.endsWith('.0') ? percentage.slice(0, -2) : percentage}%`;
  };

  // Update the label renderer to handle both formats
  const renderCustomizedLabel = (props: any) => {
    const { cx, cy, midAngle, outerRadius, value, index } = props;
    const RADIAN = Math.PI / 180;
    const x = cx + currentSize.labelRadius * Math.cos(-midAngle * RADIAN);
    const y = cy + currentSize.labelRadius * Math.sin(-midAngle * RADIAN);
    const textAnchor = x >= cx ? 'end' : 'start';

    const displayValue = valueDisplay === 'percentage' 
      ? calculatePercentage(value)
      : formatNumber(value.toFixed(0)); //remove the decimal point

    return (
      <text
        x={x}
        y={y}
        fill={colorValues[index]}
        textAnchor={textAnchor}
        dominantBaseline="central"
        className="text-[12px] font-medium"
      >
        {displayValue}
      </text>
    );
  };

  // Update the tooltip to show both value and percentage
  const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
    if (!active || !payload?.length) return null;
    const data = payload[0];
    const value = typeof data.value === 'string' ? parseFloat(data.value) : data.value || 0;
    
    return (
      <div className="bg-white p-2 border border-gray-200 rounded shadow-sm">
        <div className="flex items-center gap-2 text-sm">
          <span 
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: data.color }}
          />
          <span className="font-medium">
            {data.name}: {value.toLocaleString()}
          </span>
        </div>
      </div>
    );
  };

  // If no data, show loading waves with first color
  if (!data || Object.keys(data).length === 0) {
    const firstColor = Object.values(colors)[0];
    return (
      <div className={currentSize.containerClass}>
        <LoadingWaves color={firstColor} />
      </div>
    );
  }

  return (
    <div className={currentSize.containerClass}>
      <ResponsiveContainer width="100%" height={currentSize.height}>
        <PieChart>
          {showLegend &&          <Legend
              layout="horizontal"
              align="center"
              verticalAlign="top"
              iconType="circle"
              iconSize={8}
              formatter={(value: string) => (
                <span className="text-xs text-gray-600 rtl:text-right">
                  {value}
                </span>
              )}
            />}
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={currentSize.outerRadius}
            innerRadius={currentSize.outerRadius - 20}
            fill="#8884d8"
            dataKey="value"
            isAnimationActive={false}
            strokeWidth={0}
            startAngle={75}
            endAngle={435}
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={colorValues[index]}
              />
            ))}
          </Pie>
          <Tooltip content={CustomTooltip} />
        </PieChart>
      </ResponsiveContainer>
      {year && (
        <div className={styles.yearLabel}>{year}</div>
      )}
    </div>
  );
}; 